import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import PersonaAvatar from '../PersonaAvatar'

const HUMANS = new Set(['joel', 'bianca', 'anastasia', 'enrico', 'alee', 'tiffany'])

const ROLE_MAP: Record<string, string> = {
  forge: 'Systems Architect', mira: 'Research Lead', flux: 'Creative Director',
  griot: 'Community Lead', tack: 'Engineering Lead', vector: 'Biz Dev Lead',
  sentinel: 'Security & Infrastructure', ember: 'Web Frontend & API',
  timbre: 'Music & Sound', cairn: 'Memory & Archives', lumen: 'Neuroscience',
  relay: 'Dispatch', chronicle: 'Editor', beacon: 'Discovery & Research',
  pulse: 'Operations', herald: 'Communications', loom: 'Visual Design',
  wrench: 'Infrastructure', cadence: 'Rhythm & Coordination', reel: 'Video',
  anvil: 'iOS Development', rivet: 'Infrastructure',
  joel: 'Founder & Creative Director', bianca: 'Artist & Creative Partner',
}

const COLOR_MAP: Record<string, string> = {
  forge: '#B25943', mira: '#9C59F5', flux: '#33C7A0', griot: '#4E937A',
  tack: '#B25943', vector: '#45B7D1', sentinel: '#708090', ember: '#F59E0B',
  timbre: '#ED9429', cairn: '#7A9E7E', lumen: '#F5D623', relay: '#4493D4',
  chronicle: '#8C6DBF', beacon: '#D4A520', pulse: '#60A0B0', herald: '#C0785A',
  loom: '#7B6FA0', wrench: '#8B8B8B', cadence: '#9B7A6C', reel: '#C73E3E',
  joel: '#E6B800', bianca: '#B87DB8',
}

interface UserCardProps {
  name: string
  children: React.ReactNode
}

function capitalize(s: string) {
  return s === 'dr-claude' ? 'Dr. Claude' : s.charAt(0).toUpperCase() + s.slice(1)
}

export default function UserCard({ name, children }: UserCardProps) {
  const [show, setShow] = useState(false)
  const [pos, setPos] = useState<'below' | 'above'>('below')
  const triggerRef = useRef<HTMLSpanElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const handleEnter = () => {
    timerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        setPos(rect.bottom + 200 > window.innerHeight ? 'above' : 'below')
      }
      setShow(true)
    }, 300)
  }

  const handleLeave = () => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setShow(false), 1500)
  }

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const isHuman = HUMANS.has(name.toLowerCase())
  const role = ROLE_MAP[name.toLowerCase()] || ''
  const color = COLOR_MAP[name.toLowerCase()] || '#856FC8'

  return (
    <span
      ref={triggerRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {children}
      {show && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            [pos === 'below' ? 'top' : 'bottom']: '100%',
            marginTop: pos === 'below' ? 6 : 0,
            marginBottom: pos === 'above' ? 6 : 0,
            zIndex: 100,
            width: 220,
            background: 'var(--bg-elevated, #1c2333)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            pointerEvents: 'auto',
          }}
          onMouseEnter={() => clearTimeout(timerRef.current)}
          onMouseLeave={handleLeave}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <PersonaAvatar name={name} size={36} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                {capitalize(name)}
              </div>
              <div style={{
                fontSize: 10, fontWeight: 600,
                color: isHuman ? '#3fb950' : '#856FC8',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                {isHuman ? 'Human' : 'AI Persona'}
              </div>
            </div>
          </div>

          {role && (
            <div style={{ fontSize: 11, color: color, marginBottom: 6 }}>
              {role}
            </div>
          )}

          <Link
            to={isHuman ? '#' : `/neighborhood/${name.toLowerCase()}`}
            style={{
              display: 'block', fontSize: 10, color: 'var(--text-muted)',
              textDecoration: 'none', marginTop: 6,
              padding: '4px 8px', borderRadius: 6,
              background: 'var(--bg-body, #0d1117)',
              textAlign: 'center',
            }}
          >
            View Profile
          </Link>
        </div>
      )}
    </span>
  )
}
